import { queryKeys } from '@/services/queryClient'
import { ROOT_ID, TIERS, childTierOf, fetchAncestors, fetchChildren } from './api'

/**
 * The estate, walked one level at a time and remembered.
 *
 * ── Why this exists ──────────────────────────────────────────────────────
 * `GET /assets/hierarchy` used to hand the whole tree over in one response, and
 * two things read it: the pickers, which rendered it, and `features/users/scope.js`,
 * which COMPUTED with it — resolving "this role, at Findlay" into the per-permission
 * scope rows the API demands, which needs ancestors above a node and descendants
 * beneath it.
 *
 * That endpoint is being withdrawn. The rendering half is answered by paginating
 * (see `HierarchyPicker`), but the computing half cannot paginate: resolving a
 * grant is not a thing a person scrolls. It needs whole generations, on demand.
 * This is that — the same collection endpoints the picker drills through, walked
 * programmatically and cached.
 *
 * ── It is deliberately the same cache ────────────────────────────────────
 * Every read goes through `queryClient.fetchQuery` under `queryKeys.hierarchyChildren`,
 * which is the key `useHierarchyChildren` uses. So a generation the person has
 * already drilled into is not fetched again when the resolver reaches it, and a
 * generation the resolver pulled is already on screen when they open that branch.
 * Two walkers over one cache rather than two caches.
 *
 * ── The cost, stated ─────────────────────────────────────────────────────
 * Expanding "the whole account" to every plant is now a single request, because
 * plants hang off the account directly — it used to be one per region plus one
 * per sub-region, serial by level. Expanding to every MACHINE is still one
 * request per site plus one per line. On a large estate that is slow, and it is
 * slow in the one place the old endpoint was fast. It is the price of that
 * endpoint going away, and it is paid when a place is chosen rather than on
 * every drawer open — which is the opposite of how the tree read behaved.
 *
 * ── Root ids are normalised here and nowhere else ────────────────────────
 * The account has no endpoint, so the hierarchy uses a client-side sentinel for
 * it (`ROOT_ID`) and `fetchChildren` stamps it on every site's `parentId`. The
 * server, meanwhile, only accepts the account's real UUID in a scope. Swapping
 * one for the other at a call site is what broke the previous resolver: the root
 * got the real id, its children kept the sentinel as their parent, and every walk
 * across the account silently found nothing. So the swap happens at this seam,
 * on the way out, applied to BOTH ends of the edge.
 */

/**
 * Rows per request while walking.
 *
 * 50 because it is the page size every other caller in this repo uses, so it is
 * the one value known to be accepted. A larger page would mean fewer requests on
 * a wide generation; raise it when the API documents its maximum, not before.
 */
const WALK_PAGE_SIZE = 50

/**
 * @param {object} input
 * @param {import('@tanstack/react-query').QueryClient} input.queryClient
 * @param {{id: string, name: string}|null} input.account
 *        `null` when `GET /account` was refused — see `useEstate`. The estate
 *        still walks; it simply has no account level to offer or resolve to.
 */
export function createEstate({ queryClient, account }) {
  const root = account
    ? {
        id: account.id,
        tier: 'enterprise',
        parentId: null,
        name: account.name,
        code: null,
        isActive: true,
        readOnly: true,
      }
    : null

  /** `ROOT_ID` on the way out of the API layer becomes the account's real id. */
  const normalise = (node) => ({
    ...node,
    parentId: node.parentId === ROOT_ID ? (root?.id ?? null) : node.parentId,
  })

  /**
   * One whole generation, every page of it.
   *
   * Pages are fetched one after another rather than in parallel: `total_pages`
   * is only known once the first answer lands, and firing speculative pages at
   * a generation that usually has one would cost more requests than it saves.
   */
  async function children(parent) {
    const tier = childTierOf(parent.tier)
    if (!tier) return []

    const parentId = parent.id === root?.id ? null : parent.id
    const out = []

    for (let page = 1, pages = 1; page <= pages; page += 1) {
      const answer = await queryClient.fetchQuery({
        queryKey: queryKeys.hierarchyChildren(tier, parentId, '', page, WALK_PAGE_SIZE),
        queryFn: ({ signal }) =>
          fetchChildren({ tier, parentId, search: '', page, pageSize: WALK_PAGE_SIZE }, signal),
        staleTime: 30_000,
      })
      out.push(...answer.items.map(normalise))
      pages = answer.totalPages || 1
    }

    return out
  }

  /**
   * Root-first chain from the account down to `node`, the node itself last.
   *
   * The account is prepended rather than fetched — it has no endpoint — and is
   * omitted entirely when there is none, so a caller without `account.manage`
   * gets a chain that simply starts at the site.
   */
  async function ancestors(node) {
    if (root && node.id === root.id) return [root]

    const path = await queryClient.fetchQuery({
      queryKey: queryKeys.hierarchyAncestors(node.tier, node.id),
      queryFn: ({ signal }) => fetchAncestors({ tier: node.tier, id: node.id }, signal),
      staleTime: Infinity,
    })

    return [...(root ? [root] : []), ...path.map(normalise)]
  }

  /**
   * Every node beneath `node` at exactly `tier`.
   *
   * Breadth-first by level, because that is the shape of the requests: one call
   * per node at the level above, and nothing below the target tier is ever
   * touched. Asking for a tier at or above the node's own is not an error — it
   * is the "no descendants there" answer, which `resolvePlace` acts on.
   */
  async function descendantsAt(node, tier) {
    const want = TIERS.indexOf(tier)
    const from = TIERS.indexOf(node.tier)
    if (want <= from || from < 0) return []

    let frontier = [node]
    for (let depth = from; depth < want; depth += 1) {
      const generations = await Promise.all(frontier.map(children))
      frontier = generations.flat()
      if (!frontier.length) return []
    }
    return frontier.filter((entry) => entry.tier === tier)
  }

  return { root, children, ancestors, descendantsAt }
}
